// utils/dates/chatDate.ts
// Small date helpers for grouping chat messages by calendar day in a given time zone.

export type DateGroup<T> = {
  key: string;        // e.g. "2025-08-30" (stable per tz)
  date: Date;         // representative date (first message's local date)
  label: string;      // "Today" | "Yesterday" | "Saturday, Aug 30, 2025"
  title: string;      // full date string for title attribute (accessibility)
  items: T[];         // messages in that day (ascending)
};

type HasTimestamp = { timestamp?: string | Date };

/** Extract Y/M/D for a Date in a specific IANA time zone (no DST surprises). */
function getYMD(date: Date, timeZone: string) {
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = dtf.formatToParts(date);
  const y = Number(parts.find(p => p.type === "year")?.value);
  const m = Number(parts.find(p => p.type === "month")?.value);
  const d = Number(parts.find(p => p.type === "day")?.value);
  return { y, m, d };
}

/** Build a stable key like 2025-08-30 in the given tz. */
export function getDateKey(date: Date, timeZone: string) {
  const { y, m, d } = getYMD(date, timeZone);
  const mm = String(m).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

/** Returns "Today" / "Yesterday" / or a long, readable date label. */
export function formatDateLabel(date: Date, timeZone: string) {
  const now = new Date();
  const todayKey = getDateKey(now, timeZone);

  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const yesterdayKey = getDateKey(yesterday, timeZone);

  const key = getDateKey(date, timeZone);

  if (key === todayKey) return "Today";
  if (key === yesterdayKey) return "Yesterday";

  // Long label for other days, localized but stable.
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
}

/** Full title string (e.g., for tooltip / accessibility). */
export function formatFullDateTitle(date: Date, timeZone: string) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  }).format(date);
}

/** Safe parse: accepts ISO string or Date, returns Date (fallback: now). */
export function toDate(value: string | Date | undefined) {
  if (!value) return new Date();
  if (value instanceof Date) return value;

  // Handle "YYYY-MM-DD" as local midnight (avoid UTC drift)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    // local midnight in the runtime's TZ
    return new Date(y, m - 1, d);
  }

  // Otherwise, fall back to native parsing
  const d = new Date(value);
  return isNaN(d.getTime()) ? new Date() : d;
}

/** Group items that have a timestamp into calendar days for a tz. */
export function groupByDay<T extends HasTimestamp>(items: T[], timeZone: string): Array<DateGroup<T>> {
  const groups: Record<string, DateGroup<T>> = {};

  for (const item of items) {
    const ts = toDate(item.timestamp as any);
    const key = getDateKey(ts, timeZone);
    if (!groups[key]) {
      groups[key] = {
        key,
        date: ts,
        label: formatDateLabel(ts, timeZone),
        title: formatFullDateTitle(ts, timeZone),
        items: [],
      };
    }
    groups[key].items.push(item);
  }

  // Sort groups chronologically ascending by key, and ensure items ascending
  const sorted = Object.values(groups).sort((a, b) => (a.key < b.key ? -1 : 1));
  for (const g of sorted) {
    g.items.sort(
      (a, b) => toDate((a as any).timestamp).getTime() - toDate((b as any).timestamp).getTime()
    );
  }
  return sorted;
}



/** Return "YYYY Mon DD" (e.g., "2025 Oct 03") in a given time zone. */
export function formatYMonD(date: Date, timeZone: string) {
  const { y, m, d } = getYMD(date, timeZone); // m: 1..12 from tz-correct parts

  // ✅ no Date/UTC construction → no tz drift
  const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const mon = MONTHS_SHORT[(m - 1) as 0|1|2|3|4|5|6|7|8|9|10|11];

  const dd = String(d).padStart(2, "0");
  return `${y} ${mon} ${dd}`;
}

/** Safe wrapper: accept ISO string/Date and return "YYYY Mon DD". */
export function formatYMonDFrom(value: string | Date | undefined, timeZone: string) {
  const d = toDate(value);
  return formatYMonD(d, timeZone);
}
